import {AuthUser, UserAttributeKey} from 'aws-amplify/auth'

import {
  MaterialTechnology,
  ProviderCertificationOption,
  ProviderServiceOfferOption,
  ProviderSpecializationOption,
  ToastType,
} from '../../commons/src/types'

export enum ModalContentType {
  FATAL_ERROR = 'FATAL_ERROR',
  AUTH_CHANGE_PASSWORD = 'AUTH_CHANGE_PASSWORD',
  MATERIAL = 'MATERIAL',
  FINISH_GROUP = 'FINISH_GROUP',
  CREATE_PRICING = 'CREATE_PRICING',
  CREATE_DYNAMIC_PRODUCTION_TIME = 'CREATE_DYNAMIC_PRODUCTION_TIME',
  EDIT_DISCOUNTS = 'EDIT_DISCOUNTS',
  CREATE_SHIPPINGS = 'CREATE_SHIPPINGS',
  EDIT_SHIPPINGS = 'EDIT_SHIPPINGS',
  CREATE_INFILL = 'CREATE_INFILL',
  EDIT_INFILLS = 'EDIT_INFILLS',
  CREATE_TOLERANCE = 'CREATE_TOLERANCE',
  EDIT_TOLERANCES = 'EDIT_TOLERANCES',
  CREATE_PRODUCTION_TIME = 'CREATE_PRODUCTION_TIME',
  EDIT_PRODUCTION_TIMES = 'EDIT_PRODUCTION_TIMES',
  EDIT_PROVIDER_EMAILS = 'EDIT_PROVIDER_EMAILS',
  CONFIRM_DELETE = 'CONFIRM_DELETE',
  CONFIRM_TOGGLE_AVAILABILITY = 'CONFIRM_TOGGLE_AVAILABILITY',
  ADD_PROVIDER_INFO_DIMENSIONS = 'ADD_PROVIDER_INFO_DIMENSIONS',
  ADD_PROVIDER_INFO_PRODUCTION_TIMES = 'ADD_PROVIDER_INFO_PRODUCTION_TIMES',
  ADD_PROVIDER_INFO_PRICING = 'ADD_PROVIDER_INFO_PRICING',
  ADD_PROVIDER_INFO_PRINTING_METHOD = 'ADD_PROVIDER_INFO_PRINTING_METHOD',
  ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME = 'ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME',
  PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING = 'PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING',
  CONTACT_FORM = 'CONTACT_FORM',
  CONTACT = 'CONTACT',
  CONFIRM_DELETE_MESSAGE = 'CONFIRM_DELETE_MESSAGE',
  SIMPLE_CONTENT = 'SIMPLE_CONTENT',
  SIMPLE_CONFIRM = 'SIMPLE_CONFIRM',
  ADDITIONAL_COST = 'ADDITIONAL_COST',
  LOADING = 'LOADING',
  ENFORCE_TRACKING = 'ENFORCE_TRACKING',
  REQUEST_REFUND = 'REQUEST_REFUND',
  PARTIAL_REFUND_DETAILS = 'PARTIAL_REFUND_DETAILS',
  API_KEY = 'API_KEY',
}

export type ModalPortalProps = Record<string, unknown> & {
  fullscreenMobile?: boolean
  closeOnOutsideClick?: boolean
}
export type ModalConfigClosed = null
export type ModalConfigOpened = {
  isCloseable: boolean
  contentType: ModalContentType
  contentProps: Record<string, unknown>
}
export type ModalConfig = ModalConfigOpened | ModalConfigClosed
export type ModalMeta = ModalPortalProps & {
  isCloseable: boolean
}

export type ContactModalOption = 'email' | 'form'

export type CognitoUserAttributes = {
  email: string
}

export enum Permission {
  PROVIDER = 'provider',
  PROVIDER_OPERATOR = 'provider-operator',
  READ_ONLY = 'read-only',
  ADMIN = 'admin',
}

export type ExtendedCognitoUser = AuthUser & {
  attributes: Partial<Record<UserAttributeKey, string> & CognitoUserAttributes>
}

export type ExtendedCognitoUserWithPermission = ExtendedCognitoUser & {
  permission: Permission
}

export type Nullable<T> = T | null

export type ProviderId = string
export type ProviderInfoId = string
export type PrintingMethodId = string
export type MaterialConfigId = string
export type FinishGroupId = string
export type ColorId = string
export type PriceId = string
export type MaterialGroupId = string
export type MaterialId = string
export type ShippingId = string
export type DiscountId = string
export type OptionId = string
export type DynamicProductionTimeId = string
export type ApiPartnerMaterialId = string
export type TagId = string
export type ProviderEmailId = string

export type InternalMaterialId = string // vendor specific designation (not the id linked to a catalogue material)
export type InternalFinishId = string // vendor specific designation (not the id linked to a catalogue finish)
export type InternalMaterialName = string // configuration name the vendor will use

export type CarrierId = string

export type Dimension = [number, number, number] // x, y, z

export type ProviderInfo = {
  id: ProviderInfoId
  provider: ProviderId
  available: boolean
  materialConfigId: MaterialConfigId
  materialName: Nullable<InternalMaterialName>
  materialId: InternalMaterialId
  finishId: Nullable<InternalFinishId>
  printingMethodId: Nullable<PrintingMethodId>
  minimumDimensions: Nullable<Dimension>
  maximumDimensions: Nullable<Dimension>
  productionTimeFast: Nullable<number>
  productionTimeSlow: Nullable<number>
  priceId: Nullable<PriceId>
  dynamicProductionTimeId: Nullable<PriceId>
}

export type ProviderInfoWithOptions = ProviderInfo & {
  options: {id: OptionId}[]
}

export type MaterialConfig = {
  id: MaterialConfigId
  name: string
  finishGroupId: FinishGroupId
  colorId: ColorId
}

export type Color = {
  id: ColorId
  name: string
  hexCode: string
  look: string
  image?: string
}

export type Tag = {
  id: TagId
  name: string
}

export type FinishGroup = {
  id: FinishGroupId
  name: string
  featuredImage: string
  description: string
  descriptionShort: string
  materialId: MaterialId
  tags: Tag[]
}

export type Material = {
  id: MaterialId
  name: string
  technology: MaterialTechnology
  featuredImage: string
  description: string
  descriptionShort: string
  materialGroupId: MaterialGroupId
  printingMethods: PrintingMethod[]
  tags: Tag[]
  accuracy?: number
  accuracyLowerLimit?: number
  density?: number
  tensileStrengthMin?: number | null
  flexuralStrengthMax?: number | null
  tensileModulusMin?: number | null
  tensileModulusMax?: number | null
  tensileElongationMin?: number | null
  tensileElongationMax?: number | null
  flexuralStrengthMin?: number | null
  tensileStrengthMax?: number | null
  flexuralModulusMin?: number | null
  flexuralModulusMax?: number | null
  heatDeflectionTemp66PSIMin?: number | null
  heatDeflectionTemp66PSIMax?: number | null
  heatDeflectionTemp264PSIMin?: number | null
  heatDeflectionTemp264PSIMax?: number | null
  embossingMin?: number | null
  engravingMin?: number | null
  defaultLayerHeight?: number | null
  defaultInfill?: number | null
  interlockingParts?: boolean | null
  warpingRisk?: string | null
  maximumPrintingDimensions?: number[] | null // [x, y, z] in mm
  apps: {id: string}[]
}

export type MaterialGroup = {
  id: MaterialGroupId
  name: string
}

export type Provider = {
  id: ProviderId
  name: string
  description: string
  location: string
  enabled: boolean // master switch controlled by the craftcloud team
  online: boolean // controlled by partner
  currency: string
  email?: string
  trelloBoardId?: string
  dynamicProductionTimes: boolean
  image?: string
  minimumProductionPrice?: number
  premium: boolean
  apps: {id: string}[]
}

export enum PriceFormula {
  THREE_D_PRINTING = '3d_printing',
  CNC_MACHINING = 'cnc_machining',
}

export type Price = {
  id: PriceId
  name: string
  formula: PriceFormula
  provider: string
  basePrice: Nullable<number>
  baseUnitPrice: Nullable<number>
  boundingBoxPrice: Nullable<number>
  density: Nullable<number>
  minimumPrice: Nullable<number>
  minimumUnitPrice: Nullable<number>
  quantityDiscount: Nullable<number>
  quantityDiscountCap: Nullable<number>
  surfacePrice: Nullable<number>
  volumePrice: Nullable<number | Array<{volume: number; price: number}>>
  weightPrice: Nullable<number | Array<{weight: number; price: number}>>
  useVolumeWithSupport: boolean
  machiningCostPerHour: Nullable<number>
  machiningMaterialRemovalRate: Nullable<number>
  finishingMaterialRemovalRate: Nullable<number>
  cncAxisTier: Nullable<3 | 4 | 5>
  machineRefixtureTimePerSetup: Nullable<number>
  camProgrammingFeePerSetup: Nullable<number>
  createdAt: string // iso date
  updatedAt: string // iso date
}

export type Carrier = {
  id: CarrierId
  name: string
  createdAt: string // iso date
  updatedAt: string // iso date
}

export type ShippingType = 'standard' | 'express'
export type CountryCode = string

export type Shipping = {
  id: ShippingId
  name: string
  type: ShippingType
  deliveryTimeFast: number
  deliveryTimeSlow: number
  price: number | Array<{weight: number; price: number}>
  currency: string
  countryCode: CountryCode
  provider: ProviderId
  available: boolean
  carrierId: Nullable<CarrierId>
}

export type ShippingWithCarrier = Shipping & {
  carrier: Nullable<Omit<Carrier, 'createdAt' | 'updatedAt'>>
}

export type Country = {
  code: CountryCode
  name: string
  region: string
  blacklisted: boolean
}

export enum DiscountType {
  AMOUNT = 'amount',
  QUANTITY = 'quantity',
}

export type Discount = {
  id: DiscountId
  type: DiscountType
  threshold: number
  discount: number
  provider: ProviderId
}

export enum Infill {
  INFILL_40 = 'infill_40',
  INFILL_60 = 'infill_60',
  INFILL_80 = 'infill_80',
  INFILL_95 = 'infill_95',
  INFILL_100 = 'infill_100',
}

export enum Tolerance {
  TOLERANCE_004 = 'tolerance_004',
  TOLERANCE_003 = 'tolerance_003',
  TOLERANCE_002 = 'tolerance_002',
  TOLERANCE_001 = 'tolerance_001',
  TOLERANCE_TIGHTER_001 = 'tolerance_tighter_001',
}

export enum ProductionTimeOption {
  ECONOMY = 'economy',
  EXPEDITED = 'expedited',
}

export type OptionType = Infill | ProductionTimeOption | Tolerance

export type Option = {
  id: OptionId
  type: OptionType
  productionTimeFastTotal: Nullable<number>
  productionTimeSlowTotal: Nullable<number>
  productionTimeFastOffset: Nullable<number>
  productionTimeSlowOffset: Nullable<number>
  factor: Nullable<number>
  basePrice: Nullable<number>
  materialId: Nullable<ApiPartnerMaterialId>
  provider: ProviderId
  name: Nullable<string>
}

export type DefaultProductionTimes = {
  productionTimeFast: number
  productionTimeSlow: number
}

export type OptionWithLinkedProviderInfos = Option & {
  providerInfos: Array<{id: ProviderInfoId}>
}

export type ProviderInfoOption = {
  providerInfoId: ProviderInfoId
  optionId: OptionId
}

export type PrintingMethod = {
  id: PrintingMethodId
  name: string
  fullName: string
  clearance?: number
  minDetails?: number
  minWallThickness?: number
}

export type QuantityThresholdItem = {
  axisSize: number
  qtyThreshold: number
}

export type DynamicProductionTime = {
  id: DynamicProductionTimeId
  name: string
  provider: ProviderId
  increaseSteps: number
  maxIncrease: number
  quantityThresholds: QuantityThresholdItem[]
}

export type DynamicProductionTimeWithLinkedProviderInfos = DynamicProductionTime & {
  providerInfos: Array<{id: ProviderInfoId}>
}

export type ProviderEmail = {
  email: string
  id: ProviderEmailId
  invoices: boolean
  messages: boolean
  news: boolean
  orders: boolean
  provider: ProviderId
}

export enum EditProviderInfoTab {
  MATERIAL_NAME = 'material-name',
  PRINTING_METHOD = 'printing-method',
  DIMENSIONS = 'dimensions',
  PRODUCTION_TIME = 'production-time',
  DYNAMIC_PRODUCTION_TIME = 'dynamic-production-time',
  PRICING = 'pricing',
}

export enum SettingsTab {
  PROFILE = 'profile',
  GENERAL = 'general',
  API_KEY = 'api-key',
}

export enum PricingTab {
  THREE_D_PRINTING = '3d-printing',
  CNC_MACHINING = 'cnc-machining',
}

export type PricingRowContent = {
  price: Price
  providerInfos: ProviderInfo[]
}

export type ShippingTableRowContent = ShippingWithCarrier & {
  regionName: string
  countryName: string
  typeName: string
}

export type ProviderInfoRowContent = ProviderInfoWithOptions & {
  name: string
  technology: MaterialTechnology
  printingMethodName: string | null
  infillTypeOptions: OptionType[]
  infillOptions: OptionId[]
  toleranceTypeOptions: OptionType[]
  toleranceOptions: OptionId[]
  productionTimeTypeOptions: OptionType[]
  productionTimeOptions: OptionId[]
}

export type ProviderInfoFilterLocationState = Record<string, string[] | boolean | undefined>

export type MaterialRowContent = Material & {
  materialGroupName: string
  printingMethodNames: string[]
  printingMethodsIds: PrintingMethodId[]
  materialConfigIds: MaterialConfigId[]
  active: boolean
}

export type MinMaxRange = {
  min?: number
  max?: number
}

export type Toast = {
  id: string
  type: ToastType
  content: string
  timeout?: number
}

export type ProviderSpecialization = {
  id: string
  provider: ProviderId
  specialization: ProviderSpecializationOption
}

export type ProviderServiceOffer = {
  id: string
  provider: ProviderId
  service: ProviderServiceOfferOption
}

export type ProviderCertification = {
  id: string
  provider: ProviderId
  certification: ProviderCertificationOption
}

export type ProviderProfileImage = {
  id: string
  provider: ProviderId
  name: 'string'
  url: 'string'
  createdAt: string // iso date
}

export enum InvoicesTab {
  V1 = 'v1',
  V2 = 'v2',
}
